import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { loaded, ensureLoaded, SUPPORTED_LANGS } from './translationsLazy';
import { track } from '../lib/analytics';

const LanguageContext = createContext();

// Re-export so existing imports of SUPPORTED_LANGS keep working.
export { SUPPORTED_LANGS };

const DEFAULT_LANG = 'en';
const STORAGE_KEY = 'sv_lang';

/**
 * Pick the best initial language:
 *   1. If the user has ever explicitly chosen a language, honour it (localStorage).
 *   2. Otherwise, inspect navigator.languages / navigator.language and pick the
 *      first match against SUPPORTED_LANGS (matching on the primary subtag, so
 *      "es-AR" and "es-ES" both resolve to "es", "pt-BR" → "pt", "zh-CN" → "zh").
 *   3. Fallback to English.
 */
function detectInitialLang() {
  if (typeof window === 'undefined') return DEFAULT_LANG;

  // 1. Explicit ?lang=xx query param — used for hreflang / SEO variants and
  //    direct deep-links from Google results. Wins over every other signal
  //    so a Spanish search result always opens in Spanish on first paint.
  try {
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q && SUPPORTED_LANGS.includes(q.toLowerCase())) return q.toLowerCase();
  } catch (_) { /* malformed URL — fall through */ }

  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  } catch (_) {
    /* localStorage may be unavailable in some browsers — fall through */
  }

  const candidates = (navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || DEFAULT_LANG]);

  for (const raw of candidates) {
    if (!raw) continue;
    const primary = raw.toLowerCase().split(/[-_]/)[0];
    if (SUPPORTED_LANGS.includes(primary)) return primary;
  }
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(detectInitialLang);
  // `loadedVersion` bumps every time a lazy translation chunk finishes
  // loading, forcing consumers of `t()` and `localized()` to re-evaluate
  // against the freshly populated `loaded` table.
  const [loadedVersion, setLoadedVersion] = useState(0);

  // Lazy-load the translation chunk for the active language. English is
  // always present synchronously as the fallback, so the UI never goes
  // blank while the chunk is in flight.
  useEffect(() => {
    let cancelled = false;
    if (lang === 'en' || loaded[lang]) return undefined;
    ensureLoaded(lang).then(() => {
      if (!cancelled) setLoadedVersion((v) => v + 1);
    });
    return () => { cancelled = true; };
  }, [lang]);

  // Persist language choice so subsequent visits respect the user's selection.
  useEffect(() => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, lang);
    } catch (_) {
      /* ignore */
    }
    // Reflect on <html lang="…"> for a11y / SEO hints.
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', lang);
    }
  }, [lang]);

  const t = useCallback((key, params = {}) => {
    let text =
      loaded[lang]?.[key] ||
      loaded[DEFAULT_LANG]?.[key] ||
      key;
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, v);
    });
    return text;
    // loadedVersion is intentionally a dep so consumers re-render once the
    // lazy chunk arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, loadedVersion]);

  /**
   * Return obj[`${base}_${lang}`] with graceful fallback to English
   * and, as a last resort, the first non-empty *_xx field we find.
   *
   *   localized(exp, 'title')       → exp.title_es  (or title_en)
   *   localized(dest, 'name')       → dest.name_fr  (or name_en)
   */
  const localized = useCallback((obj, base) => {
    if (!obj) return '';
    return (
      obj[`${base}_${lang}`] ||
      obj[`${base}_en`] ||
      obj[base] ||
      ''
    );
  }, [lang]);

  const toggleLanguage = useCallback(() => {
    setLang(prev => (prev === 'en' ? 'es' : 'en'));
  }, []);

  const setLanguage = useCallback(async (newLang) => {
    if (!SUPPORTED_LANGS.includes(newLang)) return;
    if (newLang === lang) return;
    // Pre-load the lazy locale chunk BEFORE flipping the state so the UI
    // never renders one frame with the English fallback. This eliminates
    // the visible "EN flash" when switching languages. English is always
    // loaded synchronously, so the EN case is a no-op.
    if (newLang !== 'en' && !loaded[newLang]) {
      try {
        await ensureLoaded(newLang);
      } catch (_) {
        /* ensureLoaded already swallows errors and falls back to EN */
      }
    }
    setLang(newLang);
    track('language_change', { lang: newLang });
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, t, localized, toggleLanguage, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
}
